import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import './HelpButton.css';

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }

  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function HelpButton({ variant = 'floating' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [cooldownSeconds, setCooldownSeconds] = useState(null);
  const [cooldownMessage, setCooldownMessage] = useState(null);
  const cooldownIntervalRef = useRef(null);
  const { t, isRTL } = useLanguage();
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    phone: '',
    email: currentUser?.email || '',
    fullName: '',
    reason: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear status when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };

  const validatePhone = (phone) => {
    // Israeli phone number validation: 10 digits, optionally starting with 0 or +972
    const cleaned = phone.replace(/[\s-]/g, '');
    const israeliPattern = /^(\+972|0)?[1-9]\d{8}$/;
    return israeliPattern.test(cleaned);
  };

  // Cleanup cooldown interval on unmount
  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, []);

  // Handle cooldown countdown
  useEffect(() => {
    if (cooldownSeconds !== null && cooldownSeconds > 0) {
      cooldownIntervalRef.current = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current);
            }
            setCooldownMessage(null);
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
    }

    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, [cooldownSeconds]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Don't allow submission if in cooldown
    if (cooldownSeconds !== null && cooldownSeconds > 0) {
      return;
    }

    // Validation
    if (!formData.phone.trim()) {
      setSubmitStatus('error');
      return;
    }

    if (!validatePhone(formData.phone)) {
      setSubmitStatus('error');
      return;
    }

    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setSubmitStatus('error');
      return;
    }

    if (!formData.fullName.trim()) {
      setSubmitStatus('error');
      return;
    }

    if (!formData.reason.trim()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const token = currentUser ? await currentUser.getIdToken() : null;

      const response = await fetch(`${API_BASE_URL}/api/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          phone: formData.phone,
          email: formData.email,
          fullName: formData.fullName,
          reason: formData.reason
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle cooldown response (429 Too Many Requests)
        if (response.status === 429 && data.cooldown_seconds) {
          setCooldownSeconds(data.cooldown_seconds);
          setCooldownMessage(data.error || 'Please wait before submitting another request.');
          setSubmitStatus('cooldown');
          return;
        }
        throw new Error(data.error || 'Failed to send support request');
      }

      setSubmitStatus('success');
      setCooldownSeconds(null);
      setCooldownMessage(null);
      // Reset form
      setFormData({
        phone: '',
        email: currentUser?.email || '',
        fullName: '',
        reason: ''
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setIsOpen(false);
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error submitting support request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSubmitStatus(null);
    setCooldownSeconds(null);
    setCooldownMessage(null);
    // Reset form when closing
    setFormData({
      phone: '',
      email: currentUser?.email || '',
      fullName: '',
      reason: ''
    });
  };

  const HelpIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"
        fill="currentColor"
      />
    </svg>
  );

  const renderModal = () => {
    if (!isOpen) return null;

    return (
      <div className="help-modal-overlay" onClick={handleClose}>
        <div
          className={`help-modal ${isRTL ? 'rtl' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="help-modal-header">
            <h2 className="help-modal-title">
              {t('help.title') || 'Contact Support'}
            </h2>
            <button
              className="help-modal-close"
              onClick={handleClose}
              aria-label={t('common.close') || 'Close'}
            >
              ×
            </button>
          </div>

          <form className="help-modal-form" onSubmit={handleSubmit}>
            <div className="help-form-group">
              <label htmlFor="help-phone" className="help-form-label">
                {t('help.phone') || 'Phone Number'}
                <span className="help-required">*</span>
              </label>
              <input
                id="help-phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={t('help.phonePlaceholder') || '050-1234567'}
                className={`help-form-input ${submitStatus === 'error' && !formData.phone.trim() ? 'error' : ''}`}
                required
              />
            </div>

            <div className="help-form-group">
              <label htmlFor="help-email" className="help-form-label">
                {t('help.email') || 'Email'}
                <span className="help-required">*</span>
              </label>
              <input
                id="help-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t('help.emailPlaceholder') || 'your@email.com'}
                className={`help-form-input ${submitStatus === 'error' && (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) ? 'error' : ''}`}
                required
              />
            </div>

            <div className="help-form-group">
              <label htmlFor="help-fullName" className="help-form-label">
                {t('help.fullName') || 'Full Name'}
                <span className="help-required">*</span>
              </label>
              <input
                id="help-fullName"
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder={t('help.fullNamePlaceholder') || 'Your full name'}
                className={`help-form-input ${submitStatus === 'error' && !formData.fullName.trim() ? 'error' : ''}`}
                required
              />
            </div>

            <div className="help-form-group">
              <label htmlFor="help-reason" className="help-form-label">
                {t('help.reason') || 'Reason for Contact'}
                <span className="help-required">*</span>
              </label>
              <textarea
                id="help-reason"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                placeholder={t('help.reasonPlaceholder') || 'Please describe your issue or question...'}
                className={`help-form-textarea ${submitStatus === 'error' && !formData.reason.trim() ? 'error' : ''}`}
                rows="4"
                required
              />
            </div>

            {submitStatus === 'success' && (
              <div className="help-success-message">
                {t('help.successMessage') || 'Thank you! Your message has been sent. We will get back to you soon.'}
              </div>
            )}

            {submitStatus === 'cooldown' && cooldownMessage && (
              <div className="help-error-message">
                {cooldownMessage}
                {cooldownSeconds !== null && cooldownSeconds > 0 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                    {t('help.cooldownRemaining') || 'Time remaining:'} {Math.floor(cooldownSeconds / 60)}:{(cooldownSeconds % 60).toString().padStart(2, '0')}
                  </div>
                )}
              </div>
            )}

            {submitStatus === 'error' && !cooldownMessage && (
              <div className="help-error-message">
                {t('help.errorMessage') || 'Failed to send your message. Please check all fields and try again.'}
              </div>
            )}

            <div className="help-form-actions">
              <button
                type="button"
                className="help-button-cancel"
                onClick={handleClose}
                disabled={isSubmitting || (cooldownSeconds !== null && cooldownSeconds > 0)}
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                type="submit"
                className="help-button-submit"
                disabled={isSubmitting || (cooldownSeconds !== null && cooldownSeconds > 0)}
              >
                {isSubmitting
                  ? (t('help.sending') || 'Sending...')
                  : (cooldownSeconds !== null && cooldownSeconds > 0)
                  ? (t('help.cooldown') || `Please wait (${Math.floor(cooldownSeconds / 60)}:${(cooldownSeconds % 60).toString().padStart(2, '0')})`)
                  : (t('help.send') || 'Send')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // If variant is 'inline', return button styled for navigation
  if (variant === 'inline') {
    return (
      <>
        <button
          className="help-button-inline"
          onClick={() => setIsOpen(true)}
          aria-label={t('help.openHelp') || 'Open help'}
          title={t('help.openHelp') || 'Need help? Contact support'}
        >
          <span className="nav-icon">❓</span>
          <span className="nav-label">{t('help.title') || 'Help'}</span>
        </button>
        {renderModal()}
      </>
    );
  }

  // Default floating variant
  return (
    <>
      <button
        className={`help-button ${isRTL ? 'rtl' : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label={t('help.openHelp') || 'Open help'}
        title={t('help.openHelp') || 'Need help? Contact support'}
      >
        <HelpIcon />
      </button>
      {renderModal()}
    </>
  );
}

